/*
 * Users (U6) — the super-admin's GLOBAL user roster.
 *
 * Visible only to a global super-admin (`isSuperAdmin`). Lists every user
 * (`GET /auth/users`) with their super-admin flag, global disabled state, and
 * brand count, and lets the super-admin disable/enable a user globally
 * (`PATCH /auth/users/{user_id}`) — a disabled user can't log in anywhere. The
 * backend guards against disabling a super-admin or oneself (409); those controls
 * are locked here and the guard is surfaced inline.
 *
 * Distinct from Team (per-brand membership management): this is the platform
 * owner's cross-brand view. Reachable without an active brand — it is a valid
 * place a no-membership super-admin lands.
 */

import { Navigate } from "react-router-dom";
import { Text } from "@mantine/core";
import {
  Alert,
  Button,
  PageHeader,
  Table,
  type Column,
} from "../../../components/ui";
import { StatusPill } from "../../../components/StatusPill";
import { Section } from "../../../components/config/Section";
import { useAuth } from "../../../auth/AuthContext";
import { ApiError } from "../../../api/client";
import { errorMessage } from "../../../api/errors";
import type { UserRosterEntry } from "../../../api/client";
import { useUsers, useUpdateUser } from "./hooks";

const DESCRIPTION =
  "Every user on the platform. Disable a user to block their login everywhere; re-enable to restore access. Super-admins can't be disabled.";

export function UsersPage() {
  const { isSuperAdmin, user } = useAuth();
  const usersQuery = useUsers({ enabled: isSuperAdmin });
  const update = useUpdateUser();

  // Defense in depth: the route is also nav-gated, but a deep link must not show.
  if (!isSuperAdmin) return <Navigate to="/" replace />;

  const users = usersQuery.data ?? [];
  const mutateError = userMutationError(update.error);

  const columns: Column<UserRosterEntry>[] = [
    {
      key: "email",
      header: "User",
      render: (u) => (
        <span>
          {u.email}
          {u.id === user?.id && (
            <Text component="span" c="dimmed" fz="sm">
              {" "}
              (you)
            </Text>
          )}
        </span>
      ),
    },
    {
      key: "kind",
      header: "Kind",
      render: (u) =>
        u.is_super_admin ? (
          <StatusPill tone="info" label="Super-admin" />
        ) : (
          <StatusPill tone="neutral" label="User" />
        ),
    },
    {
      key: "brands",
      header: "Brands",
      align: "right",
      render: (u) => <span>{u.brand_count}</span>,
    },
    {
      key: "status",
      header: "Status",
      render: (u) =>
        u.disabled ? (
          <StatusPill tone="danger" label="Disabled" />
        ) : (
          <StatusPill tone="success" label="Active" />
        ),
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      render: (u) => {
        // A super-admin can't be disabled, and the caller can't disable itself.
        const locked = u.is_super_admin || u.id === user?.id;
        const busy = update.isPending && update.variables?.userId === u.id;
        return (
          <Button
            variant={u.disabled ? "secondary" : "danger"}
            size="sm"
            disabled={locked && !u.disabled}
            loading={busy}
            onClick={() =>
              update.mutate({ userId: u.id, body: { disabled: !u.disabled } })
            }
          >
            {u.disabled ? "Enable" : "Disable"}
          </Button>
        );
      },
    },
  ];

  return (
    <>
      <PageHeader title="Users" description={DESCRIPTION} />

      <Section
        title="All users"
        description="The global roster across every brand."
      >
        {mutateError && <Alert tone="error">{mutateError}</Alert>}

        {usersQuery.isError ? (
          <Alert tone="error">{errorMessage(usersQuery.error)}</Alert>
        ) : usersQuery.isLoading ? (
          <Text c="dimmed" fz="sm">
            Loading users…
          </Text>
        ) : (
          <Table
            columns={columns}
            rows={users}
            rowKey={(u) => u.id}
            empty="No users yet."
          />
        )}
      </Section>
    </>
  );
}

/**
 * Turn a user-mutation error into an inline message. The backend guards against
 * disabling a super-admin / oneself (409).
 */
function userMutationError(err: unknown): string | null {
  if (err == null) return null;
  if (err instanceof ApiError && err.status === 409) {
    const body = err.body;
    if (body && typeof body === "object" && "detail" in body) {
      const detail = (body as { detail: unknown }).detail;
      if (typeof detail === "string") return detail;
    }
    return "A super-admin can't be disabled, and you can't disable yourself.";
  }
  return errorMessage(err);
}

export default UsersPage;
